import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconCheck,
  IconCurrencyEuro,
  IconDotsVertical,
  IconDownload,
  IconFileText,
  IconPlus,
  IconReceipt,
  IconRefresh,
  IconSearch,
  IconShieldCheck,
} from "@tabler/icons-react";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { setBalanceAmountSignFromType } from "../../utils";
import {
  useBalanceTypes,
  useTenantAccountBalance,
  useTenantBalances,
  // @ts-expect-error pure js
} from "../../hooks/use-balances";
// @ts-expect-error pure js
import useFiles from "../../hooks/use-files";
// @ts-expect-error pure js
import BalanceForm from "../Balance/BalanceForm";
import {
  BalanceDomain,
  BalanceType,
  BalanceTypeCategory,
  DG_CALL_TYPES,
  RENT_CALL_TYPES,
} from "../../utils/constants/balances";
import {
  getBalanceText,
  getPeriodAnchorMonth,
  isStandaloneTenantCall,
} from "../../utils/balances";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
import TenantDecompteDialog from "./TenantDecompteDialog";
// @ts-expect-error pure js
import BalanceMasseForm from "../Balance/BalanceMasseForm";
import RegenerateBalanceForm from "../Balance/RegenerateBalanceForm";
import BulkQuittanceModal from "./Bulk/BulkQuittanceModal";
// @ts-expect-error pure js
import { useConfirm } from "../../hooks/use-confirm.jsx";
import useDocumentDownload, {
  type DownloadableFile,
} from "../../hooks/use-document-download";

import Button from "../UI/Button";
import DataCard from "../UI/DataCard";
import DropdownMenu from "../UI/DropdownMenu.tsx";
import BalanceTable from "../Balance/BalanceTable";
import BalanceFilterBar from "../Balance/BalanceFilterBar";
import useBalanceFilters from "../../hooks/use-balance-filters";
import type { Balance } from "../../models/balance";
import BalanceRowActions from "../Balance/BalanceRowActions";
import AvoirForm from "../Balance/AvoirForm.tsx";
import CardAction from "../UI/CardAction";
import Title from "../UI/Title";
import styles from "./TenantPayments.module.css";
import Callout from "../UI/Callout.tsx";
import type { Tenant } from "../../models/tenant";

interface TenantPaymentsProps {
  tenant: Tenant | null;
  isLocataire?: boolean;
  syncTransactionHandler?: () => void;
}

function QuittanceDownloadItem({
  quittance,
}: {
  quittance: DownloadableFile & { id: number };
}) {
  const { downloadHandler } = useDocumentDownload({
    documentId: quittance.id,
    file: quittance,
    save: true,
  });
  return (
    <>
      <DropdownMenu.Item
        icon={IconDownload}
        label="Télécharger la quittance"
        onClick={downloadHandler}
      />
    </>
  );
}

export default function TenantPayments({
  tenant,
  isLocataire = false,
  syncTransactionHandler,
}: TenantPaymentsProps) {
  const nav = useNavigate();
  const {
    balances,
    deleteBalance,
    updateBalance,
    createBalance,
    duplicateBalance,
    batchUpdateBalances,
    regenerateBalances,
    setUpToDateBalances,
  } = useTenantBalances(tenant?.id, isLocataire);
  const { balances: balanceTenant, mutate: refreshBalanceTenant } =
    useTenantAccountBalance(tenant?.id, isLocataire);
  const { balanceTypes, balanceTypesByCategory, isLoading } = useBalanceTypes();
  const { files } = useFiles({ tenantId: tenant?.id });

  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [isDecompteOpen, setIsDecompteOpen] = useState(false);
  const [isBatchUpdateOpen, setIsBatchUpdateOpen] = useState(false);
  const [isRegenerateOpen, setIsRegenerateOpen] = useState(false);
  const [Confirm, openConfirm] = useConfirm();

  const [isQuittanceModalOpen, setIsQuittanceModalOpen] = useState(false);
  const [AvoirModal, closeAvoirModal, openAvoirModal] = useModal();
  const [balanceToEdit, setBalanceToEdit] = useState<Balance | undefined>();
  const [avoirBalance, setAvoirBalance] = useState<Balance | null>(null);
  const decompteYears = useMemo(() => {
    const years: number[] = balances.map((balance: Balance) =>
      new Date(balance.date_transac).getFullYear(),
    );
    return [...new Set(years)].sort((a, b) => b - a);
  }, [balances]);
  const [showBalance, setShowBalance] = useState(false);

  const quittances = useMemo(
    () => files.filter((f: { tag: string }) => f.tag === "quittance"),
    [files],
  );

  const getBalanceLabel = (balance: Balance) =>
    getBalanceText(balance, {
      balanceTypes,
      domaineBalance: BalanceDomain.TENANT,
      paymentRecurrence: tenant?.payment_recurrence,
      periodAnchorMonth: getPeriodAnchorMonth(tenant),
    });
  const balanceFilters = useBalanceFilters({
    balances,
    getBalanceLabel,
    getTypeLabel: (type: string) => balanceTypes?.[type]?.text ?? "",
  });

  if (isLoading || !balanceTenant) return null;

  async function submitBalanceHandler(
    edit: boolean,
    values: Record<string, unknown>,
  ) {
    const sanitizedValues = { ...sanitizeValues(values) };
    sanitizedValues.montant = setBalanceAmountSignFromType(
      sanitizedValues,
      balanceTypesByCategory,
      sanitizedValues.is_remboursement,
    );
    delete sanitizedValues.is_remboursement;

    if (edit && balanceToEdit) {
      await updateBalance(balanceToEdit.id, sanitizedValues);
    } else {
      await createBalance(sanitizedValues);
    }
    await refreshBalanceTenant();
    setIsBalanceModalOpen(false);
  }

  async function markAsPaid(balance: Balance) {
    try {
      const appelStaticIds =
        balanceTypesByCategory[BalanceTypeCategory.LOCATAIRE_APPEL]?.map(
          (bt: { static_id: string }) => bt.static_id,
        ) || [];

      const isAgencyFee = (type: string) =>
        balanceTypes?.[type]?.is_agency_fee ?? false;
      const newBalance: Partial<Balance> = { ...balance };
      delete newBalance.id;

      if (DG_CALL_TYPES.includes(balance.type)) {
        newBalance.montant = -balance.montant;
        newBalance.type = BalanceType.PAIEMENT_DG;
      } else if (isAgencyFee(balance.type)) {
        // Les honoraires dus à l'agence se règlent à part du loyer.
        newBalance.montant = -balance.montant;
        newBalance.type = BalanceType.PAIEMENT_HONORAIRES;
      } else if (isStandaloneTenantCall(balance, balanceTypes)) {
        newBalance.montant = -balance.montant;
        newBalance.type = BalanceType.PAIEMENT;
      } else {
        newBalance.montant = balances
          .filter(
            (b: Balance) =>
              moment(b.date_transac).isSame(balance.date_transac, "month") &&
              appelStaticIds.includes(b.type) &&
              !isAgencyFee(b.type),
          )
          .reduce((acc: number, curr: Balance) => acc - curr.montant, 0);
        newBalance.type = BalanceType.PAIEMENT;
      }
      await createBalance(newBalance);
      await refreshBalanceTenant();
    } catch (error) {
      console.error(error);
    }
  }

  async function handleDelete(balance: Balance) {
    const isGrouped = balance.balances && balance.balances.length > 0;
    const warningTypes = [
      ...RENT_CALL_TYPES,
      BalanceType.APPEL_CHARGE,
      BalanceType.APPEL_COMPLEMENTAIRE,
    ];

    const message = warningTypes.includes(balance.type)
      ? "Attention ! Les appels de loyer / charge ne devraient pas être supprimés ! Ils sont nécessaires pour la génération des quittances et la bonne tenue de la comptabilité locataire."
      : "Voulez-vous vraiment supprimer cette entrée ?";

    if (window.confirm(message)) {
      try {
        await deleteBalance(isGrouped ? balance.correlation_id : balance.id);
        await refreshBalanceTenant();
      } catch (error) {
        console.error(error);
      }
    }
  }

  async function submitMasseBalanceHandler(updatedBalances: Balance[]) {
    await batchUpdateBalances(updatedBalances);
    setIsBatchUpdateOpen(false);
  }

  function getQuittanceForBalance(balance: Balance) {
    return quittances?.find((q: { meta: { date_quittance: string } }) =>
      moment(q.meta.date_quittance, "YYYY-MM-DD").isSame(
        balance.date_transac,
        "month",
      ),
    );
  }

  const shouldShowMarkAsPaid = (balance: Balance) =>
    [...RENT_CALL_TYPES, ...DG_CALL_TYPES].includes(balance.type) ||
    isStandaloneTenantCall(balance, balanceTypes);

  const renderRowActions = (balance: Balance) => {
    if (isLocataire) return null;
    const quittance = getQuittanceForBalance(balance);
    return (
      <BalanceRowActions
        balance={balance}
        onEdit={() => {
          setBalanceToEdit(balance);
          setIsBalanceModalOpen(true);
        }}
        onDuplicate={async () => {
          await duplicateBalance(balance.id);
          await refreshBalanceTenant();
        }}
        onDelete={() => handleDelete(balance)}
      >
        {shouldShowMarkAsPaid(balance) && (
          <DropdownMenu.Item
            requiresWrite
            icon={IconCheck}
            label="Marquer comme payé"
            variant="success"
            onClick={() => markAsPaid(balance)}
          />
        )}
        {quittance && <QuittanceDownloadItem quittance={quittance} />}
        {/*<DropdownMenu.Item icon={IconMail} label="Envoyer une quittance" />*/}
        {/*<DropdownMenu.Item*/}
        {/*  icon={IconAlertCircle}*/}
        {/*  label="Faire un avis d'échéance"*/}
        {/*/>*/}
        {balance.is_protected_by_invoice &&
          balance.type !== BalanceType.AVOIR_REMISE && (
            <DropdownMenu.Item
              requiresWrite
              icon={IconArrowBackUp}
              label="Annuler la facture (avoir)"
              onClick={() => {
                setAvoirBalance(balance);
                openAvoirModal();
                console.log("mqlsdkfhoiqushdfloiqushdpfoiqshdj^foi");
              }}
            />
          )}
      </BalanceRowActions>
    );
  };

  return (
    <>
      <AvoirForm
        ModalElement={AvoirModal}
        balance={avoirBalance}
        onSubmit={() => {
          setAvoirBalance(null);
          closeAvoirModal();
          return refreshBalanceTenant();
        }}
      />
      <BalanceForm
        balance={balanceToEdit}
        domaineBalance={BalanceDomain.TENANT}
        parentId={tenant?.id}
        open={isBalanceModalOpen}
        onOpenChange={(open: boolean) => {
          setIsBalanceModalOpen(open);
          if (!open) setBalanceToEdit(undefined);
        }}
        onSubmit={(values: Record<string, unknown>) =>
          submitBalanceHandler(balanceToEdit !== undefined, values)
        }
        onDelete={async (id: number) => {
          try {
            await deleteBalance(id);
            setIsBalanceModalOpen(false);
          } catch (error) {
            console.error(error);
          }
        }}
      />
      <TenantDecompteDialog
        open={isDecompteOpen}
        onOpenChange={setIsDecompteOpen}
        tenant={tenant}
        years={decompteYears}
      />
      <BalanceMasseForm
        balances={balances}
        open={isBatchUpdateOpen}
        onOpenChange={setIsBatchUpdateOpen}
        onSubmit={submitMasseBalanceHandler}
        domaineBalance={BalanceDomain.TENANT}
      />
      {tenant && (
        <RegenerateBalanceForm
          open={isRegenerateOpen}
          onOpenChange={setIsRegenerateOpen}
          tenant={tenant}
          onSubmit={async (values: Record<string, unknown>) => {
            await regenerateBalances(values);
            await refreshBalanceTenant();
          }}
        />
      )}
      <Confirm
        onAnswer={async (ok: boolean) => {
          if (ok) {
            try {
              await setUpToDateBalances();
            } catch (error) {
              console.error(error);
            }
          }
        }}
        size="small"
      >
        <>
          <Callout variant="warning">
            Ne pas utiliser cette fonction si vous utilisez la synchronisation
            bancaire
          </Callout>
          <p>
            Cette fonction ajoute un paiement pour chaque mois ayant un
            moins-perçu. Ne pas utiliser cette fonction si votre locataire est
            en impayé. En cas de trop-perçu sur un mois, vous devez éditer
            manuellement la ligne correspondante.
          </p>
        </>
      </Confirm>

      {tenant && (
        <BulkQuittanceModal
          open={isQuittanceModalOpen}
          onOpenChange={setIsQuittanceModalOpen}
          lockedTenantId={tenant.id}
        />
      )}

      {/* Data Cards */}
      <DataCard.Grid>
        <DataCard
          title="Solde du compte locataire"
          value={currencyFormatter(balanceTenant.balance, {
            withSymbol: false,
          })}
          valueSuffix="€"
          icon={IconCurrencyEuro}
          status={balanceTenant.balance >= 0 ? "success" : "danger"}
          statusLabels={{
            success: "À jour",
            danger: "En retard",
          }}
        />
        <DataCard
          title="Dépôt de garantie encaissé"
          value={currencyFormatter(balanceTenant.dg_debit, {
            withSymbol: false,
            noDecimals: true,
          })}
          valueSuffix={`/${currencyFormatter(-balanceTenant.dg_credit, {
            withSymbol: false,
            noDecimals: true,
          })}€`}
          icon={IconShieldCheck}
          status={
            balanceTenant.dg_debit >= -balanceTenant.dg_credit
              ? "success"
              : "warning"
          }
          statusLabels={{
            success: "Complet",
            warning: "Incomplet",
          }}
        />
        <DataCard
          title="Somme des loyers perçus"
          value={currencyFormatter(balanceTenant.debits, {
            withSymbol: false,
          })}
          valueSuffix="€"
          icon={IconCurrencyEuro}
        />
      </DataCard.Grid>

      {!isLocataire && (
        <div className={styles.raccourcisSection}>
          <Title
            variant="deca"
            weight="bold"
            as="h2"
            className={styles.sectionTitle}
          >
            Mes raccourcis
          </Title>
          <div className={styles.raccourcisGrid}>
            <CardAction
              requiresWrite
              icon={IconReceipt}
              title={
                tenant?.is_type_quittance
                  ? "Générer une quittance"
                  : "Générer une facture acquittée"
              }
              onClick={() => setIsQuittanceModalOpen(true)}
            />
            <CardAction
              requiresWrite
              icon={IconRefresh}
              title="Mettre à jour les loyers"
              onClick={openConfirm}
            />
            <CardAction
              requiresWrite
              icon={IconAlertTriangle}
              title={
                tenant?.is_type_quittance
                  ? "Avis d'échéance"
                  : "Générer une facture"
              }
              onClick={() =>
                nav(
                  `/bailleur/tenants/${tenant?.id}?tab-tenant-show=actions&action=courrier&avenant-type=avis_echeance`,
                )
              }
            />
            <CardAction
              requiresWrite
              icon={IconShieldCheck}
              title="Rembourser / Conserver le dépôt de garantie"
              onClick={() =>
                nav(
                  `/bailleur/tenants/${tenant?.id}?tab-tenant-show=actions&action=courrier&avenant-type=restitution_dg`,
                )
              }
            />
          </div>
        </div>
      )}

      {/* Locataire toggle */}
      {isLocataire && (
        <div className={styles.toggleSection}>
          <Button
            variant="tertiary"
            onClick={() => setShowBalance((prev) => !prev)}
          >
            {showBalance
              ? "Masquer le détail de la balance"
              : "Afficher le détail de la balance"}
          </Button>
        </div>
      )}

      {/* Balance Tables by Month */}
      {(showBalance || !isLocataire) && balances && (
        <>
          <BalanceFilterBar
            filters={balanceFilters}
            leftActions={
              isLocataire ? undefined : (
                <>
                  <Button
                    requiresWrite
                    icon={IconPlus}
                    onClick={() => {
                      setBalanceToEdit(undefined);
                      setIsBalanceModalOpen(true);
                    }}
                  >
                    Ajouter une opération
                  </Button>
                  <DropdownMenu
                    icon={IconDotsVertical}
                    label="Plus d'options"
                    showLabel
                  >
                    <DropdownMenu.Item
                      icon={IconReceipt}
                      label="Télécharger le décompte locataire"
                      onClick={() => setIsDecompteOpen(true)}
                    />
                    {syncTransactionHandler && (
                      <DropdownMenu.Item
                        requiresWrite
                        icon={IconSearch}
                        label="Rechercher les transactions"
                        onClick={syncTransactionHandler}
                      />
                    )}
                    <DropdownMenu.Item
                      requiresWrite
                      icon={IconRefresh}
                      label="Regénérer l'historique de paiement"
                      onClick={() => setIsRegenerateOpen(true)}
                    />
                    <DropdownMenu.Item
                      requiresWrite
                      icon={IconFileText}
                      label="Édition groupée"
                      onClick={() => setIsBatchUpdateOpen(true)}
                    />
                  </DropdownMenu>
                </>
              )
            }
          />
          <BalanceTable
            balances={balanceFilters.filteredBalances}
            getBalanceText={getBalanceLabel}
            renderRowActions={isLocataire ? undefined : renderRowActions}
          />
        </>
      )}
    </>
  );
}
